import { useMemo, useState } from 'react';
import { ArrowRight, Hash } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { Option } from '@/constants/institute';
import { classDisplayName } from '@/features/institute/classes/utils';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type { InstituteClass } from '@/types/instituteClass';
import type { RollNumberMode, RollNumberOrder } from '@/types/student';
import { studentsApi } from '../api/students.api';
import { ROLL_NUMBER_MODE_OPTIONS, ROLL_NUMBER_ORDER_OPTIONS } from '../constants';

/**
 * Numbering a section's roll, one section at a time.
 *
 * Roll numbers are no longer typed in on the admission form: a section is
 * numbered all at once, in one order, so 9-B cannot end up with two 14s and no
 * 15. The office picks the section and the order, sees exactly who gets which
 * number, and only then confirms. The server works the plan out again when it
 * saves, so a child admitted between preview and confirm is numbered too.
 */

export interface AssignRollNumbersModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** This session's classes, already loaded by the roster for its filters. */
  classes: InstituteClass[];
  /** The roster re-reads, so the new numbers show straight away. */
  onAssigned: () => void;
}

export function AssignRollNumbersModal({ isOpen, onClose, classes, onAssigned }: AssignRollNumbersModalProps) {
  const [sectionUuid, setSectionUuid] = useState('');
  const [order, setOrder] = useState<RollNumberOrder>('FIRST_NAME');
  const [mode, setMode] = useState<RollNumberMode>('MISSING');
  const [isSaving, setIsSaving] = useState(false);

  /** "1st Class - A", as the classes roster names a section. */
  const sectionOptions: Option[] = useMemo(
    () =>
      classes.flatMap((instituteClass) =>
        instituteClass.sections.map((section) => ({
          value: section.uuid,
          label: `${classDisplayName(instituteClass.name)} - ${section.name}`,
        })),
      ),
    [classes],
  );

  const {
    data: plan,
    isLoading,
    error,
    refetch,
  } = useApiQuery(
    () =>
      isOpen && sectionUuid
        ? studentsApi.previewRollNumbers({ sectionUuid, order, mode })
        : Promise.resolve(null),
    [isOpen, sectionUuid, order, mode],
  );

  const changes = plan?.changes ?? [];
  /** Children who already had a number and would get a different one. */
  const renumbered = changes.filter((change) => change.from !== null).length;

  /** Starts the next run from a clean slate rather than the last section's choices. */
  function reset() {
    setSectionUuid('');
    setOrder('FIRST_NAME');
    setMode('MISSING');
    onClose();
  }

  function close() {
    if (!isSaving) reset();
  }

  async function assign() {
    setIsSaving(true);
    try {
      const saved = await studentsApi.assignRollNumbers({ sectionUuid, order, mode });
      const label = `${classDisplayName(saved.section.className)} - ${saved.section.name}`;
      toast.success(
        saved.changes.length === 1 ? '1 roll number assigned' : `${saved.changes.length} roll numbers assigned`,
        `${label} · attendance, marks and hall tickets use them from now on.`,
      );
      onAssigned();
      setIsSaving(false);
      reset();
    } catch (caught) {
      toast.error(
        'Could not assign roll numbers',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
      setIsSaving(false);
      // Another office may have numbered the section meanwhile; show what is true now.
      refetch();
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Assign roll numbers"
      description="Number a section in one go. You will see every number before anything is saved."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void assign()} isLoading={isSaving} disabled={!plan || changes.length === 0 || isLoading}>
            {changes.length > 0
              ? `Assign ${changes.length === 1 ? '1 roll number' : `${changes.length} roll numbers`}`
              : 'Assign roll numbers'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Select
              label="Section"
              required
              placeholder={sectionOptions.length > 0 ? 'Choose a section' : 'No sections this year'}
              options={sectionOptions}
              disabled={sectionOptions.length === 0 || isSaving}
              value={sectionUuid}
              onChange={(event) => setSectionUuid(event.target.value)}
            />
          </div>
          <Select
            label="Order"
            options={ROLL_NUMBER_ORDER_OPTIONS}
            disabled={isSaving}
            value={order}
            onChange={(event) => setOrder(event.target.value as RollNumberOrder)}
          />
          <Select
            label="Who to number"
            options={ROLL_NUMBER_MODE_OPTIONS}
            disabled={isSaving}
            value={mode}
            onChange={(event) => setMode(event.target.value as RollNumberMode)}
            hint={
              mode === 'MISSING'
                ? 'New numbers continue after the highest one already given.'
                : 'Everyone gets a new number, starting from 1.'
            }
          />
        </div>

        {!sectionUuid && (
          <EmptyState
            icon={<Hash className="h-5 w-5" />}
            title="Choose a section"
            description="The students in it, and the roll number each would get, appear here."
          />
        )}

        {sectionUuid && isLoading && <SkeletonLines count={5} />}

        {sectionUuid && error && (
          <QueryError title="Could not work out the roll numbers" error={error} onRetry={refetch} />
        )}

        {sectionUuid && !isLoading && !error && plan && changes.length === 0 && (
          <EmptyState
            icon={<Hash className="h-5 w-5" />}
            title={plan.unchanged > 0 ? 'Everyone already has a roll number' : 'No students in this section'}
            description={
              plan.unchanged > 0
                ? 'To number the section again in a different order, choose “Renumber the whole section from 1”.'
                : 'Admit students to this section first, then number them here.'
            }
          />
        )}

        {sectionUuid && !isLoading && !error && changes.length > 0 && (
          <>
            {renumbered > 0 && (
              <Alert tone="warning" title={`${renumbered === 1 ? '1 student’s' : `${renumbered} students’`} roll number will change`}>
                Printed attendance registers, mark lists and hall tickets will show the old numbers until they are
                printed again.
              </Alert>
            )}

            <div className="card overflow-hidden">
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">Roll numbers to be assigned</caption>
                  <thead className="sticky top-0 bg-surface-2">
                    <tr className="text-left">
                      <th scope="col" className="lbl px-4 py-2">Roll no.</th>
                      <th scope="col" className="lbl px-4 py-2">Student</th>
                      <th scope="col" className="lbl hidden px-4 py-2 sm:table-cell">Admission no.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.map((change) => (
                      <tr key={change.uuid} className="border-t border-line">
                        <td className="whitespace-nowrap px-4 py-2 font-mono tabular-nums">
                          {change.from !== null && (
                            <>
                              <span className="text-ink-3 line-through">{change.from}</span>
                              <ArrowRight className="mx-1 inline h-3 w-3 text-ink-3" aria-label="becomes" />
                            </>
                          )}
                          <span className="font-bold text-ink">{change.to}</span>
                        </td>
                        <td className="px-4 py-2 text-ink">{change.fullName}</td>
                        <td className="hidden px-4 py-2 font-mono text-ink-2 sm:table-cell">{change.admissionNumber}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {plan && plan.unchanged > 0 && (
              <p className="text-sm text-ink-2">
                {plan.unchanged === 1 ? '1 student keeps' : `${plan.unchanged} students keep`} the roll number they
                already have.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
